/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-props-no-spreading */
import React, {useEffect, useState} from 'react';
import {TrendingUp, TrendingDown, RefreshCw, X, Filter} from 'lucide-react';
import dayjs from 'dayjs';
import numeral from 'numeral';
import cn from 'helpers/cn';
import {useService} from 'services/hooks/useService';
import movementsService from 'services/movements';
import categoriesService from 'services/categories';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isoWeek from 'dayjs/plugin/isoWeek';

import {
  ArcElement,
  PointElement,
  Tooltip,
  Legend,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';

import ChartJS from 'chart.js/auto';

import {Chart, Doughnut} from 'react-chartjs-2';
import {avgLast} from 'helpers/data';
import Select from 'components/UI/Select';
import MovementForm from 'modules/MovementForm';
import PullToRefresh from 'react-simple-pull-to-refresh';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs';

dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);

ChartJS.register(
  ArcElement,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
);

const colors = [
  '#10b981', // Emerald 500
  '#3b82f6', // Blue 500
  '#f59e0b', // Amber 500
  '#ef4444', // Red 500
  '#8b5cf6', // Violet 500
  '#ec4899', // Pink 500
  '#06b6d4', // Cyan 500
  '#64748b', // Slate 500
];

const itemsSelectdayGroupBy = [
  {
    value: 'day',
    label: 'Day',
  },
  {
    value: 'week',
    label: 'Week',
  },
];

function HomeModule() {
  const [showEditMovement, setShowEditMovement] = useState<Movement>();
  const [filterConceptByCategory, setFilterConceptByCategory] =
    useState<string>();
  const [dayGroupBy, setDayGroupBy] = useState<SelectItem<string>>(
    itemsSelectdayGroupBy[0],
  );

  useEffect(() => {
    const container = document.getElementById('tab-container');
    if (container !== null) {
      container.scroll({top: 0, behavior: 'smooth'});
    }
  }, [filterConceptByCategory]);

  const {
    response: movementsStats,
    status: statusMovements,
    loader: loadMovements,
  } = useService<MovementStats>(async () => movementsService.filter({}), true);

  useEffect(() => {
    loadMovements();
  }, []);

  const {response: allCategories} = useService<Category[]>(
    async () => categoriesService.all(),
    true,
  );

  const filteredCategories = (allCategories ?? []).filter(
    (category) => category.category_type.direction === -1,
  );

  if (statusMovements === 'executing') {
    return (
      <div className='text-zinc-500 text-center h-[80vh] grid justify-center items-center'>
        <div className='flex flex-col gap-4 items-center'>
          <RefreshCw className='animate-spin text-primary' size={32} />
          <div className='text-sm font-medium'>Loading your expenses...</div>
        </div>
      </div>
    );
  }

  const movements = movementsStats?.movements;
  const categories = movementsStats?.categories;
  let concepts = movementsStats?.concepts;

  if (filterConceptByCategory) {
    concepts = concepts?.filter(
      (c) => c.category.name === filterConceptByCategory,
    );
  }
  const days = movementsStats?.days ?? [];
  const nDays = days?.length ?? 1;
  const total = movements?.reduce((a, x) => a + x.amount, 0) ?? 0;

  const movementsByDay: Record<string, Movement[]> =
    movements?.reduce(
      (acc, movement) => {
        const day = movement.date
          ? dayjs(movement.date).format('YYYY-MM-DD')
          : null;
        if (!day) {
          return acc;
        }
        if (!(day in acc)) {
          acc[day] = [];
        }
        acc[day].push(movement);
        return acc;
      },
      {} as Record<string, Movement[]>,
    ) || {};

  let barDays = null;

  if (dayGroupBy.value === 'day') {
    barDays = [...days].reverse();
  } else if (dayGroupBy.value === 'week') {
    const byWeek = [...days].reverse().reduce(
      (acc, day) => {
        const newAcc = acc;
        const week = dayjs(day.day).format('GGGG-WW');
        if (!(week in acc)) {
          acc[week] = {
            ...day,
            day: week,
          };
        } else {
          acc[week].total += day.total;
        }
        return newAcc;
      },
      {} as Record<string, DayStats>,
    );
    barDays = Object.values(byWeek);
  }

  const avgL7DTotal =
    days.slice(1, 8).reduce((a, x) => a + x.total, 0) /
    Math.max(1, days.slice(1, 8).length);

  const barDaysWithAvg: DayStatsAvg[] = avgLast(
    barDays,
    7,
    'total',
    'avgTotalL7D',
  );

  const dailyBudget = 25000;

  const deltaVsBudget = total / nDays / dailyBudget - 1;
  const deltaVsBudgetL7D = avgL7DTotal / dailyBudget - 1;

  return (
    <PullToRefresh
      onRefresh={loadMovements}
      refreshingContent={
        <div className='p-8 text-center'>
          <RefreshCw className='animate-spin mx-auto text-primary' size={24} />
        </div>
      }
    >
      <div className='flex flex-col gap-6 pb-20'>
        {/* Header Summary */}
        <header className='flex flex-col gap-4'>
          <div className='card-premium p-6 overflow-hidden relative'>
            {/* Background Glow Signature */}
            <div
              className={cn(
                'absolute -top-24 -right-24 w-48 h-48 blur-[80px] rounded-full opacity-20',
                deltaVsBudget > 0 ? 'bg-red-500' : 'bg-emerald-500',
              )}
            />

            <div className='flex flex-col gap-1 relative z-10'>
              <span className='text-sm font-medium text-muted-foreground'>
                Total Spent
              </span>
              <div className='text-4xl font-extrabold tracking-tighter'>
                {numeral(total).format('$0,0')}
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4 mt-8 pt-6 border-t border-border/50 relative z-10'>
              <div className='flex flex-col gap-1'>
                <span className='text-xxs uppercase tracking-wider text-muted-foreground font-semibold'>
                  Daily Avg
                </span>
                <span className='text-lg font-bold'>
                  {numeral(total / nDays).format('$0,0')}
                </span>
                <div
                  className={cn(
                    'flex items-center gap-1 text-xs font-medium',
                    deltaVsBudget > 0 ? 'text-red-500' : 'text-emerald-500',
                  )}
                >
                  {deltaVsBudget > 0 ? (
                    <TrendingUp size={14} />
                  ) : (
                    <TrendingDown size={14} />
                  )}
                  {numeral(deltaVsBudget).format('+0%')}{' '}
                  <span className='text-muted-foreground/70 font-normal text-[10px]'>
                    vs budget
                  </span>
                </div>
              </div>
              <div className='flex flex-col gap-1 text-right'>
                <span className='text-xxs uppercase tracking-wider text-muted-foreground font-semibold'>
                  L7D Avg
                </span>
                <span className='text-lg font-bold'>
                  {numeral(avgL7DTotal).format('$0,0')}
                </span>
                <div
                  className={cn(
                    'flex items-center gap-1 justify-end text-xs font-medium',
                    deltaVsBudgetL7D > 0 ? 'text-red-500' : 'text-emerald-500',
                  )}
                >
                  {deltaVsBudgetL7D > 0 ? (
                    <TrendingUp size={14} />
                  ) : (
                    <TrendingDown size={14} />
                  )}
                  {numeral(deltaVsBudgetL7D).format('+0%')}{' '}
                  <span className='text-muted-foreground/70 font-normal text-[10px]'>
                    vs budget
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className='relative h-full overflow-hidden'>
          <Tabs
            defaultValue='byDay'
            className='h-full flex flex-col'
            onValueChange={() => {
              const container = document.getElementById('tab-container');
              if (container !== null) {
                container.scroll({top: 0, behavior: 'smooth'});
              }
            }}
          >
            <TabsList className='grid grid-cols-4 glass p-0 rounded-xl'>
              <TabsTrigger
                value='byDay'
                className='text-xs py-2 rounded-lg data-[state=active]:bg-slate-200/50 shadow-sm'
              >
                By Day
              </TabsTrigger>
              <TabsTrigger
                value='byCategory'
                className='text-xs py-2 rounded-lg data-[state=active]:bg-slate-200/50 shadow-sm'
              >
                Category
              </TabsTrigger>
              <TabsTrigger
                value='byConcept'
                className='text-xs py-2 rounded-lg data-[state=active]:bg-slate-200/50 shadow-sm'
              >
                Concept
              </TabsTrigger>
              <TabsTrigger
                value='details'
                className='text-xs py-2 rounded-lg data-[state=active]:bg-slate-200/50 shadow-sm'
              >
                Details
              </TabsTrigger>
            </TabsList>

            <div className='mt-6 h-full' id='tab-container'>
              <TabsContent value='byCategory'>
                <div className='flex flex-col gap-8'>
                  <div className='flex justify-center p-4 card-premium'>
                    <Doughnut
                      width={200}
                      height={200}
                      data={{
                        labels: categories?.map(
                          (category: CategoryStats) => category.category.name,
                        ),
                        datasets: [
                          {
                            data: categories?.map(
                              (category: CategoryStats) => category.total,
                            ),
                            backgroundColor: categories?.map(
                              (_, ix) => colors[ix % colors.length],
                            ),
                            borderColor: 'transparent',
                            hoverOffset: 10,
                          },
                        ],
                      }}
                      options={{
                        plugins: {
                          legend: {display: false},
                          tooltip: {
                            backgroundColor: 'rgba(0,0,0,0.8)',
                            padding: 12,
                            cornerRadius: 8,
                            callbacks: {
                              label(context) {
                                let label = '';
                                const {parsed, dataIndex} = context;
                                const category = categories?.[dataIndex];
                                label += `${numeral(category?.total).format('$0,0')} (${numeral(parsed / total).format('0%')})`;
                                return label;
                              },
                            },
                          },
                        },
                        cutout: '75%',
                      }}
                    />
                  </div>

                  <div className='flex flex-col gap-2'>
                    {categories?.map((category: CategoryStats, i: number) => (
                      <div
                        key={i}
                        className='card-premium p-4 flex items-center justify-between'
                      >
                        <div className='flex gap-4 items-center'>
                          <div
                            className='w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold'
                            style={{
                              backgroundColor: colors[i % colors.length],
                            }}
                          >
                            {category.category.name[0]}
                          </div>
                          <div className='flex flex-col'>
                            <span className='text-xs text-muted-foreground uppercase tracking-wider font-semibold'>
                              {category.category?.group}
                            </span>
                            <span className='font-bold'>
                              {category.category.name}
                            </span>
                          </div>
                        </div>
                        <div className='flex flex-col items-end'>
                          <span className='font-bold text-lg'>
                            {numeral(category.total).format('$0,0')}
                          </span>
                          <span className='text-xs font-medium text-muted-foreground'>
                            {numeral(category.total / total).format('0%')} of
                            total
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value='byConcept'>
                <div className='flex flex-col gap-4'>
                  {filterConceptByCategory && (
                    <div className='card-premium p-3 bg-amber-500/10 border-amber-500/20 flex justify-between items-center'>
                      <div className='flex gap-2 items-center text-amber-600 dark:text-amber-400 text-sm font-medium'>
                        <Filter size={16} />
                        <span>Category: {filterConceptByCategory}</span>
                      </div>
                      <button
                        type='button'
                        onClick={() => setFilterConceptByCategory(undefined)}
                        className='text-amber-600 dark:text-amber-400 p-1 rounded-full hover:bg-amber-500/10'
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}

                  <div className='grid grid-cols-3 gap-4 mb-4'>
                    <div className='card-premium p-4 flex flex-col items-center'>
                      <span className='text-xxs uppercase font-bold text-muted-foreground mb-1'>
                        Total
                      </span>
                      <span className='font-bold truncate w-full text-center'>
                        {numeral(
                          concepts?.reduce((acc, x) => x.total + acc, 0) ?? 0,
                        ).format('$0,0')}
                      </span>
                    </div>
                    <div className='card-premium p-4 flex flex-col items-center'>
                      <span className='text-xxs uppercase font-bold text-muted-foreground mb-1'>
                        Mvts
                      </span>
                      <span className='font-bold'>
                        {concepts?.reduce((acc, x) => x.movements + acc, 0) ??
                          0}
                      </span>
                    </div>
                    <div className='card-premium p-4 flex flex-col items-center'>
                      <span className='text-xxs uppercase font-bold text-muted-foreground mb-1'>
                        Types
                      </span>
                      <span className='font-bold'>{concepts?.length ?? 0}</span>
                    </div>
                  </div>

                  <div className='flex flex-col gap-2'>
                    {concepts?.map((concept: ConceptStats, i: number) => (
                      <div
                        key={i}
                        className='card-premium p-4 flex items-center justify-between transition-all active:scale-[0.98]'
                      >
                        <div className='flex gap-3 items-center overflow-hidden'>
                          <div
                            className='w-2 h-10 rounded-full'
                            style={{backgroundColor: colors[i % colors.length]}}
                          />
                          <div className='flex flex-col overflow-hidden'>
                            <button
                              type='button'
                              className='text-[10px] text-primary/70 uppercase font-bold tracking-tight text-left truncate'
                              onClick={() =>
                                setFilterConceptByCategory(
                                  concept.category.name,
                                )
                              }
                            >
                              {concept.category.name}
                            </button>
                            <span className='font-bold text-sm truncate'>
                              {concept.concept}
                            </span>
                          </div>
                        </div>
                        <div className='flex flex-col items-end min-w-[80px]'>
                          <span className='font-bold'>
                            {numeral(concept.total).format('$0,0')}
                          </span>
                          <span className='text-[10px] text-muted-foreground'>
                            {numeral(concept.total / total).format('0%')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value='byDay'>
                <div className='flex flex-col gap-6'>
                  <div className='card-premium p-6'>
                    <div className='flex justify-between items-center mb-6'>
                      <span className='text-sm font-bold tracking-tight'>
                        Spending Trend
                      </span>
                      <div className='w-[100px]'>
                        <Select
                          items={itemsSelectdayGroupBy}
                          value={dayGroupBy}
                          onChange={(value) => setDayGroupBy(value)}
                        />
                      </div>
                    </div>
                    <Chart
                      type='bar'
                      height={180}
                      data={{
                        labels: barDaysWithAvg.map((day) => day.day),
                        datasets: [
                          {
                            data: barDaysWithAvg.map((day) => day.total),
                            backgroundColor: barDaysWithAvg.map((day) => {
                              if (day.total <= dailyBudget)
                                return `${colors[0]}cc`;
                              if (day.total <= dailyBudget * 1.5)
                                return `${colors[2]}cc`;
                              return `${colors[3]}cc`;
                            }),
                            borderRadius: 6,
                            barThickness: 12,
                          },
                          {
                            data: barDaysWithAvg.map((day) => day.avgTotalL7D),
                            borderColor: colors[1],
                            borderWidth: 2,
                            pointRadius: 0,
                            tension: 0.4,
                            type: 'line',
                          },
                        ],
                      }}
                      options={{
                        plugins: {
                          legend: {display: false},
                          tooltip: {
                            backgroundColor: 'rgba(0,0,0,0.8)',
                            padding: 12,
                            cornerRadius: 8,
                            callbacks: {
                              label(context) {
                                const {dataIndex} = context;
                                const day = barDaysWithAvg?.[dataIndex];
                                return [
                                  `Total: ${numeral(day?.total).format('$0,0')}`,
                                  `Avg: ${numeral(day?.avgTotalL7D).format('$0,0')}`,
                                ];
                              },
                            },
                          },
                        },
                        scales: {
                          y: {display: false},
                          x: {
                            grid: {display: false},
                            ticks: {font: {size: 10}},
                          },
                        },
                      }}
                    />
                  </div>

                  <div className='flex flex-col gap-2'>
                    {days?.map((day, i: number) => (
                      <div
                        key={i}
                        className='card-premium p-4 flex items-center justify-between'
                      >
                        <span className='font-bold text-sm'>
                          {dayjs(day.day).format('ddd, MMM D')}
                        </span>
                        <div className='flex items-center gap-4'>
                          <span className='text-xs text-muted-foreground'>
                            {day.movements} movements
                          </span>
                          <span className='font-bold'>
                            {numeral(day.total).format('$0,0')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value='details'>
                <div className='flex flex-col gap-8'>
                  {Object.keys(movementsByDay).map((day, i: number) => {
                    const movementsInDay = movementsByDay[day];
                    const dayTotal = movementsInDay.reduce(
                      (a, c) => a + c.amount,
                      0,
                    );
                    return (
                      <div key={i} className='flex flex-col gap-3'>
                        <div className='flex justify-between items-center px-2'>
                          <span className='text-sm font-extrabold text-muted-foreground uppercase tracking-widest'>
                            {dayjs(day).format('dddd, D MMM')}
                          </span>
                          <span className='font-bold text-sm bg-secondary px-3 py-1 rounded-full'>
                            {numeral(dayTotal).format('$0,0')}
                          </span>
                        </div>
                        <div className='flex flex-col gap-2'>
                          {movementsInDay?.map(
                            (movement: Movement, ix: number) => (
                              <div
                                key={ix}
                                className='card-premium p-4 flex justify-between items-center group active:scale-[0.98] transition-all'
                                onClick={() => setShowEditMovement(movement)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter')
                                    setShowEditMovement(movement);
                                }}
                                role='button'
                                tabIndex={0}
                              >
                                <div className='flex flex-col gap-0.5'>
                                  <span className='font-bold text-sm group-hover:text-primary transition-colors'>
                                    {movement.concept}
                                  </span>
                                  <span className='text-xs text-muted-foreground font-medium'>
                                    {movement.category?.group} •{' '}
                                    {movement.category?.name}
                                  </span>
                                </div>
                                <span className='font-extrabold text-base'>
                                  {numeral(movement.amount).format('$0,0')}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {showEditMovement && (
          <div className='fixed inset-0 z-[100] flex items-end justify-center px-4 pb-0 bg-black/40 backdrop-blur-sm transition-all duration-300 animate-in fade-in'>
            <div className='w-full max-w-[500px] glass rounded-t-3xl p-6 shadow-2xl animate-in slide-in-from-bottom duration-300 relative'>
              <div className='w-12 h-1 bg-muted rounded-full mx-auto mb-6' />
              <div className='mb-6 flex justify-between items-center'>
                <h2 className='text-xl font-bold tracking-tight'>
                  Edit Movement
                </h2>
                <button
                  type='button'
                  onClick={() => setShowEditMovement(undefined)}
                  className='p-2 rounded-full hover:bg-secondary transition-colors'
                >
                  <X size={20} />
                </button>
              </div>
              <MovementForm
                categories={filteredCategories ?? []}
                defaultValues={showEditMovement}
                onSaving={() => {}}
                onSave={() => {
                  setShowEditMovement(undefined);
                  loadMovements();
                }}
                onError={() => {}}
              />
            </div>
          </div>
        )}
      </div>
    </PullToRefresh>
  );
}

export default HomeModule;
